import ResponsiveSidebar from "../../General/ResponsiveSidebar/ResponsiveSidebar";
import React from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Groups as GroupsIcon,
  AccessTime as AccessTimeIcon,
  AccountCircle as AccountCircleIcon,
  EventAvailable as EventAvailableIcon,
  Event as EventIcon,
  FolderShared as FolderSharedIcon,
  AttachMoney as AttachMoneyIcon,
  Menu as MenuIcon,
  Description as DescriptionIcon,
  Work as WorkIcon,
  Assessment as AssessmentIcon,
  Calculate as CalculateIcon,
  Tune as TuneIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  WorkHistory as WorkHistoryIcon,
  ChevronLeft as ChevronLeftIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";

const drawerWidth = 240;
const collapsedWidth = 60;

interface SidebarProps {
  open?: boolean;
  toggle?: () => void;
}

const HRSidebar = ({ open = false, toggle = () => {} }: SidebarProps) => {
  const location = useLocation();
  const { role } = useAuth();

  const employeeMenuItems = [
    { text: "My profile", icon: <AccountCircleIcon />, path: "/hr" },
    {
      text: "Availability",
      icon: <EventAvailableIcon />,
      path: "/hr/availability",
    },
    {
      text: "External Events",
      icon: <EventIcon />,
      path: "/hr/external-events",
    },
    {
      text: "Open Positions",
      icon: <WorkIcon />,
      path: "/hr/open-positions",
    },
  ];
  const hrMenuItems = [
    { text: "Employees", icon: <GroupsIcon />, path: "/hr/employees" },
    {
      text: "Promotions",
      icon: <WorkHistoryIcon />,
      path: "/hr/promotions",
    },
    {
      text: "Time Off Requests",
      icon: <AccessTimeIcon />,
      path: "/hr/time-off-requests",
    },
    {
      text: "Time Off Calculations",
      icon: <CalculateIcon />,
      path: "/hr/time-off-calculations",
    },
    {
      text: "PTO Accrual Policies",
      icon: <TuneIcon />,
      path: "/hr/pto-accrual-policies",
    },
    {
      text: "Resignation Letters",
      icon: <DescriptionIcon />,
      path: "/hr/resignation-letters",
    },
    {
      text: "Worked Time Summary",
      icon: <AssessmentIcon />,
      path: "/hr/worked-time-summary",
    },
  ];
  const headOfHRMenuItems = [
    { text: "Documents", icon: <FolderSharedIcon />, path: "/hr/documents" },
  ];
  const employeeFinancialsMenuItems = [
    {
      text: "Employee Financials",
      icon: <AttachMoneyIcon />,
      path: "/hr/employee-financials",
    },
    {
      text: "Exchange Rates",
      icon: <CurrencyExchangeIcon />,
      path: "/hr/exchange-rates",
    },
  ];

  const menuItems: { text: string; icon: React.ReactNode; path: string }[] = [];
  if (role?.includes("Employee")) menuItems.push(...employeeMenuItems);
  if (role?.includes("HR")) menuItems.push(...hrMenuItems);
  if (role?.includes("HeadOfHR")) menuItems.push(...headOfHRMenuItems);
  if (role?.includes("EmployeeFinancials"))
    menuItems.push(...employeeFinancialsMenuItems);

  const isSelected = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      <ResponsiveSidebar
        open={open}
        onClose={toggle}
        sx={(theme) => ({
          width: open ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            boxSizing: "border-box",
            top: 64,
            height: "calc(100% - 64px)",
            overflowX: "hidden",
            position: "fixed",
            transition: theme.transitions.create("width", {
              duration: theme.transitions.duration.standard,
            }),
          },
        })}
      >
        <Toolbar sx={{ justifyContent: open ? "flex-end" : "center" }}>
          <IconButton
            onClick={toggle}
            aria-label={
              open ? "Close section navigation" : "Open section navigation"
            }
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Toolbar>
        <List>
          {menuItems.map((item) => {
            const button = (
              <ListItemButton
                key={item.text}
                selected={isSelected(item.path)}
                component={RouterLink}
                to={item.path}
                sx={{ justifyContent: open ? "initial" : "center" }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 0,
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            );
            return open ? (
              button
            ) : (
              <Tooltip key={item.text} title={item.text} placement="right">
                {button}
              </Tooltip>
            );
          })}
        </List>
      </ResponsiveSidebar>
    </>
  );
};

export default HRSidebar;
